import React, { useState, useEffect, useCallback, useRef } from "react";
import { AbilityPhase, processAbilityInteractions } from "./abilities";
import { RoleDistributorSocketService } from "./RoleDistributorSocketService";
import { RolePlayer } from "./RoleDistributorReducer";
import { useTranslation } from "react-i18next";

// localStorage 키 정의
const ABILITY_PHASE_DURATION_KEY = "roleDistributor_abilityPhaseDuration";

interface Props {
  abilityPhase: AbilityPhase;
  isHost: boolean;
  socketService: RoleDistributorSocketService | null;
  hostId: number;
  allPlayers: RolePlayer[];
  onShowResults?: (results: any[]) => void;
}

const AbilityPhaseControl: React.FC<Props> = ({
  abilityPhase,
  isHost,
  socketService,
  hostId,
  allPlayers,
  onShowResults,
}) => {
  const { t } = useTranslation();

  // 타이머 입력 상태 (초 단위) - 빈값으로 시작, localStorage에서 불러옴
  const [durationInput, setDurationInput] = useState<string>(() => {
    const saved = localStorage.getItem(ABILITY_PHASE_DURATION_KEY);
    return saved || "";
  });

  // 남은 시간 표시용 상태 (밀리초)
  const [remainingTime, setRemainingTime] = useState<number | null>(null);

  // 자동 종료 처리 플래그 (중복 호출 방지)
  const autoEndTriggeredRef = useRef(false);

  // 페이즈 종료
  const handleEndPhase = useCallback(() => {
    if (!socketService || !isHost) return;

    if (abilityPhase.pendingResults.length > 0) {
      const processedResults = processAbilityInteractions(
        abilityPhase.pendingResults,
        allPlayers,
        t
      );
      if (onShowResults) {
        onShowResults(processedResults);
      }
    }

    socketService.endAbilityPhase(hostId);
  }, [socketService, isHost, abilityPhase.pendingResults, allPlayers, t, onShowResults, hostId]);

  // 타이머 카운트다운 효과
  useEffect(() => {
    if (!abilityPhase.isActive || !abilityPhase.endTime) {
      setRemainingTime(null);
      autoEndTriggeredRef.current = false;
      return;
    }

    const updateRemainingTime = () => {
      const now = Date.now();
      const remaining = Math.max(0, abilityPhase.endTime! - now);
      setRemainingTime(remaining);

      // 타이머 종료 시 자동으로 액션 페이즈 종료 (호스트만)
      if (remaining <= 0 && isHost && socketService && !autoEndTriggeredRef.current) {
        autoEndTriggeredRef.current = true;
        handleEndPhase();
      }
    };

    // 즉시 한 번 실행
    updateRemainingTime();

    // 100ms마다 업데이트 (더 부드러운 타이머)
    const interval = setInterval(updateRemainingTime, 100);

    return () => clearInterval(interval);
  }, [abilityPhase.isActive, abilityPhase.endTime, isHost, socketService, handleEndPhase]);

  // 타이머 입력값 저장
  const handleDurationChange = (value: string) => {
    setDurationInput(value);
    localStorage.setItem(ABILITY_PHASE_DURATION_KEY, value);
  };

  // 페이즈 시작 (바로 시작) - 빈값이면 60초 기본값 적용
  const handleStartPhase = useCallback(() => {
    if (!socketService || !isHost) return;

    // 빈값이면 60초 기본값 사용
    const inputValue = durationInput.trim();
    const duration = inputValue === "" ? 60 : parseInt(inputValue, 10);

    if (isNaN(duration) || duration <= 0) {
      alert(t("RoleDistributor.invalidDuration", "올바른 시간을 입력해주세요."));
      return;
    }

    // 사용한 값 저장 (다음에 기억)
    localStorage.setItem(ABILITY_PHASE_DURATION_KEY, duration.toString());

    socketService.startAbilityPhase(hostId, duration);
  }, [socketService, isHost, durationInput, hostId, t]);

  // 남은 시간 포맷팅
  const formatRemainingTime = (ms: number): string => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0
      ? `${minutes}:${seconds.toString().padStart(2, "0")}`
      : `${seconds}`;
  };

  // 호스트가 아니면 타이머만 표시 (액션 진행중일 때만)
  if (!isHost) {
    if (!abilityPhase.isActive) return null;

    return (
      <div
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-mono font-bold ${
          remainingTime !== null && remainingTime < 10000
            ? "bg-red-600 text-white animate-pulse"
            : "bg-gray-700 text-yellow-400"
        }`}
      >
        <span><i className="fas fa-clock" /></span>
        {remainingTime !== null ? (
          <span>{formatRemainingTime(remainingTime)}</span>
        ) : (
          <span className="text-gray-400">{t("RoleDistributor.noTimeLimit", "시간 제한 없음")}</span>
        )}
      </div>
    );
  }

  // 호스트 UI - 타이머 입력 항상 표시 + 시작/종료 버튼
  return (
    <div className="flex items-center gap-2">
      {/* 타이머 입력 (대기중일 때) 또는 카운트다운 표시 (진행중일 때) */}
      {!abilityPhase.isActive ? (
        // 대기중: 타이머 입력 필드
        <div className="flex items-center gap-1">
          <input
            type="number"
            min="1"
            max="3600"
            value={durationInput}
            onChange={(e) => handleDurationChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleStartPhase();
              }
            }}
            className="w-16 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-sm"
            placeholder="60"
          />
          <span className="text-gray-400 text-sm">
            {t("RoleDistributor.seconds", "초")}
          </span>
        </div>
      ) : (
        // 진행중: 카운트다운 표시
        remainingTime !== null && (
          <div
            className={`px-3 py-1.5 rounded-lg font-mono font-bold text-sm ${
              remainingTime < 10000
                ? "bg-red-600 text-white animate-pulse"
                : "bg-gray-700 text-yellow-400"
            }`}
          >
            <span><i className="fas fa-clock mr-1.5" /></span>
            {formatRemainingTime(remainingTime)}
          </div>
        )
      )}

      {/* 시작/종료 버튼 - 조건부 렌더링으로 FontAwesome removeChild 에러 방지 */}
      {abilityPhase.isActive ? (
        <button
          onClick={handleEndPhase}
          className="px-3 py-1.5 rounded-lg font-medium text-white text-sm transition-colors flex items-center gap-1.5 bg-red-500 hover:bg-red-600"
        >
          <span><i className="fas fa-stop-circle" /></span>
          {t("RoleDistributor.endAction", "액션 종료")}
        </button>
      ) : (
        <button
          onClick={handleStartPhase}
          className="px-3 py-1.5 rounded-lg font-medium text-white text-sm transition-colors flex items-center gap-1.5 bg-green-500 hover:bg-green-600"
        >
          <span><i className="fas fa-play-circle" /></span>
          {t("RoleDistributor.startAction", "액션 시작")}
        </button>
      )}
    </div>
  );
};

export default AbilityPhaseControl;
